import { logger } from 'src/utils/logger'

// Tracks the native file picker (camera / gallery / files) so we can see in LogRocket when
// Android kills the page while the picker is open: the page reloads and the file never arrives.
// The pending record lives in sessionStorage so it survives that reload.

const PENDING_PICKER_STORAGE_KEY = 'file_picker_pending'
const PENDING_PICKER_MAX_AGE_MS = 60 * 60 * 1000

interface PendingFilePicker {
  inputId: string
  openedAt: number
  path: string
}

const readPendingPicker = (): PendingFilePicker | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_PICKER_STORAGE_KEY)

    if (!raw) {
      return null
    }

    return JSON.parse(raw) as PendingFilePicker
  } catch {
    return null
  }
}

const clearPendingPicker = () => {
  try {
    sessionStorage.removeItem(PENDING_PICKER_STORAGE_KEY)
  } catch {}
}

const getSecondsSince = (timestamp: number) => {
  return Math.round((Date.now() - timestamp) / 1000)
}

const getNavigationType = () => {
  const [entry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]

  return entry?.type ?? null
}

export const trackFilePickerOpened = (inputId: string, accept: string) => {
  const pending: PendingFilePicker = {
    inputId,
    openedAt: Date.now(),
    path: window.location.pathname,
  }

  try {
    sessionStorage.setItem(PENDING_PICKER_STORAGE_KEY, JSON.stringify(pending))
  } catch {}

  logger.track('file_picker_opened', { inputId, accept })
}

export const trackFilePickerFileSelected = (inputId: string, file: File) => {
  const pending = readPendingPicker()

  clearPendingPicker()

  logger.track('file_picker_file_selected', {
    inputId,
    secondsInPicker: pending ? getSecondsSince(pending.openedAt) : null,
    fileType: file.type || null,
    fileSizeKb: Math.round(file.size / 1024),
    // A file modified seconds ago was most likely just taken with the camera;
    // an old one was picked from the gallery / files.
    fileAgeSeconds: file.lastModified ? getSecondsSince(file.lastModified) : null,
  })
}

export const trackFilePickerCancelled = (inputId: string) => {
  const pending = readPendingPicker()

  clearPendingPicker()

  logger.track('file_picker_cancelled', {
    inputId,
    secondsInPicker: pending ? getSecondsSince(pending.openedAt) : null,
  })
}

// Call once on app start. A pending picker left over from before this page load means the
// page was reloaded while the picker was open — the picked file (if any) was lost.
export const trackFilePickerPageReload = () => {
  const pending = readPendingPicker()

  if (!pending) {
    return
  }

  clearPendingPicker()

  const elapsedMs = Date.now() - pending.openedAt

  if (elapsedMs > PENDING_PICKER_MAX_AGE_MS) {
    return
  }

  logger.track('file_picker_page_reloaded', {
    inputId: pending.inputId,
    secondsSincePickerOpened: Math.round(elapsedMs / 1000),
    pickerPath: pending.path,
    samePath: pending.path === window.location.pathname,
    navigationType: getNavigationType(),
  })
}
